<template>
  <div>
    <div 
      class="py-2 font-bold"
      :class="isForMarkupDialog ? 'item-container-markup' : 'item-container'"
    >
      <div class="flex justify-between items-start">
        <div class="flex flex-col">
          <div class="flex">
            <p v-if="showQuantityBadge && quantity > 0" class="quantity mr-2">{{ quantity + 'x' }}</p>
            <p class="name" :style="{ color: nameColor }"> {{ name }}</p>
          </div>
          <div v-if="testNameList.length > 0" class="text-sm font-normal mt-1">
            <div
              class="flex items-center cursor-pointer"
              @click="isShowIncludedTests = !isShowIncludedTests"
            >
              <p v-if="testNameList.length === 1">
                {{ $t('OrderPage["Includes {count} test."]', { count: testNameList.length }) }}
              </p>
              <p v-else>
                {{ $t('OrderPage["Includes {count} tests."]', { count: testNameList.length }) }}
              </p>
              <span class="material-icons-outlined expand-icon cursor-pointer">
                {{ isShowIncludedTests ? 'expand_less' : 'expand_more' }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-col items-end justify-start">
          <div class="flex items-center">
            <div
              v-if="showQuantityControls && !isForMarkupDialog"
              class="quantity-control mr-2"
            >
              <button type="button" class="quantity-btn" @click="$emit('decreaseQuantity')">−</button>
              <span class="quantity-value">{{ quantity }}</span>
              <button type="button" class="quantity-btn" @click="$emit('increaseQuantity')">+</button>
            </div>
            <slot name="original-price" v-if="showOriginalPrice">
              <p class="final-price" :class="isForMarkupDialog ? '' : 'mr-2.5'">
                {{ currency.symbol + originalPrice.toFixed(2) }}
              </p>
            </slot>
            <span
              v-if="clear && !isForMarkupDialog"
              class="material-icons-outlined clear-icon cursor-pointer"
              @click="$emit('clear')"
            >
              clear
            </span>
          </div>
        </div>
      </div>
      <ul v-if="isShowIncludedTests && testNameList.length > 0" class="ml-2 list-disc list-inside mt-1">
        <li v-for="(testName, index) in testNameList" :key="index">
          {{ testName }}
        </li>
      </ul>
    </div>
    <div 
      v-if="showDeal" 
      class="flex justify-between items-center flex-1 min-w-0"
      :class="isForMarkupDialog ? 'unlocking-deal-container-markup' : 'unlocking-deal-container'"
    >
      <div class="flex justify-between">
        <img class="unlocking-img ml-4" src="@/assets/orderTest/order-page/order-test-panel/unlocking-deal.svg">
        <p class="text-sm font-normal unlocking-text ml-4">
          {{ $t('OrderPage["0~~Unlocking deal <saved {amount}>"]', { amount: currency.symbol + (originalPrice - currentPrice).toFixed(2) }) }}
          <span class="font-bold save-text">
            {{ $t('OrderPage["1Nested~~Unlocking deal <saved {amount}>"]', { amount: currency.symbol + (originalPrice - currentPrice).toFixed(2) }) }}
          </span>
        </p>
      </div>
      <p 
        class="font-bold deal-price" 
        :class="isForMarkupDialog ? '' : 'mr-8'"
        style="height: 16px;"
      >
        {{ currency.symbol + currentPrice.toFixed(2) }}
      </p>
    </div>
    <div class="flex justify-between" :class="isForMarkupDialog ? 'mr-2' : ''">
      <div class="flex items-center">
        <div 
          v-if="isCalculated && isShowMarkupPrice"
          class="flex markup-btn italic cursor-pointer ml-3"
          @click="isShowWhyCalculatedDialog = true"  
        >
          Why Calculated?
        </div>        
      </div>
      <slot name="markup">
        <div v-if="isShowMarkupPrice">
          <div v-if="markupPrice" class="markup-text">
            <div class="flex items-center">
              <p :class="isCheckoutPage ? '' :'mr-2'">{{ markupText }} {{ currency.symbol + markupPrice.toFixed(2) }}</p>
              <img 
                v-if="isShowEditMarkup && !isCheckoutPage && isMarkupChangeable"
                src="@/assets/orderTest/order-page/edit_16px.svg" 
                alt="edit" 
                class="cursor-pointer" 
                @click="$emit('editMarkup')"
              />
              <p 
                v-if="isForMarkupDialog && (!sameAsDefaultMarkupPrice || customizedTestNameChanged ) && !isCalculated" 
                class="markup-btn cursor-pointer ml-3" 
                @click="saveAsDefault"
              >
                Save as default
              </p>
            </div>
          </div>
          <div v-else class="markup-btn cursor-pointer">
            <p
              v-if="isForMarkupDialog && hasOriginalPrice" 
              @click="addOriginalMarkup"
            >
              {{ 'Add back to ' + currency.symbol + defaultMarkupPrice.toFixed(2) }}
            </p>
            <p v-else-if="isMarkupChangeable" @click="$emit('addMarkup')">Edit Price</p>
          </div>
        </div>
      </slot>      
    </div>
    <WhyCalculatedDialog 
      :is-show-dialog="isShowWhyCalculatedDialog"
      :name="name"
      :markup-price="markupPrice"
      :test-name-list="testNameList"
      :test-markup-price-list="testMarkupPriceList"
      @close="close"
      @saveMarkupAsDefault="saveAsDefault"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import WhyCalculatedDialog from "./WhyCalculatedDialog.vue";

export default {
  components: {
    WhyCalculatedDialog
  },

  emits: ["clear", "editMarkup", "addMarkup", "saveMarkup", "saveMarkupAsDefault", "increaseQuantity", "decreaseQuantity"],
  props: {
    name: {
      type: String,
      default: ""
    },
    originalPrice: {
      type: Number,
      default: 0
    },
    currentPrice: {
      type: Number,
      default: 0
    },
    clear: {
      type: Boolean,
      default: true
    },
    showDeal: {
      type: Boolean,
      default: false
    },
    testNameList: {
      type: Array,
      default: () => []
    },
    testMarkupPriceList: {
      type: Array,
      default: () => []
    },
    nameColor: {
      type: String,
      default: null
    },
    markupPrice: {
      type: Number,
      default: 0
    },
    customizedTestNameChanged: {
      type: Boolean,
      default: false
    },
    markupVersion: {
      type: Number,
      default: null,
    },
    isShowMarkupPrice: {
      type: Boolean,
      default: false
    },
    defaultMarkupPrice: {
      type: Number,
      default: 0
    },
    isForMarkupDialog: {
      type: Boolean,
      default: false,
    },
    isShowEditMarkup: {
      type: Boolean,
      default: true
    },
    isMarkupChangeable: {
      type: Boolean,
      default: true,
    },
    quantity: {
      type: Number,
      default: 0,
    },
    showQuantityBadge: {
      type: Boolean,
      default: true,
    },
    showQuantityControls: {
      type: Boolean,
      default: false,
    },
    showOriginalPrice: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      isShowIncludedTests: false,
      isShowWhyCalculatedDialog: false,
    }
  },

  computed: {
    ...mapState(["currency"]),
    isCheckoutPage() {
      return window.location.href.includes("/checkout");
    },
    sameAsDefaultMarkupPrice() {
      if (!this.hasOriginalPrice) {
        return false;
      }
      return this.markupPrice.toFixed(2) === this.defaultMarkupPrice.toFixed(2);
    },
    hasOriginalPrice() {
      return this.defaultMarkupPrice && this.defaultMarkupPrice > 0;
    },
    isCalculated() {
      if (this.markupPrice.toFixed(2) === this.defaultMarkupPrice.toFixed(2)) {
        return false;
      }
      return this.markupVersion == -1
    },
    markupText() {
      if (this.isCalculated) {
        return "Calculated Revenue";
      }
      return "Revenue";
    }
  },

  methods: {
    saveAsDefault() {
      this.$emit("saveMarkupAsDefault");
    },

    addOriginalMarkup() {
      let price = Math.round(Number(this.defaultMarkupPrice) *100) / 100;
      this.$emit("saveMarkup", { markup: price, customizedTestName: null });
    },
    close() {
      this.isShowWhyCalculatedDialog = false;
    }
  }
};
</script>

<style scoped>
.item-container {
  min-height: 34px;
  /* width: 349px; */
  /* border-radius: 3px; */
}

.item-container-markup {
  min-height: 40px;
}

.name {
  max-width: 207px;
  font-size: 14px;
  letter-spacing: 0.4px;
  line-height: 16px;

  word-break: break-word;
}

.quantity {
  height: 16px;
  color: var(--color-Black-000, #000);
  text-align: center;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */

  display: flex;
  padding: 0px 5px;
  align-items: center;
  background: var(--color-Functional-Grey-status-001, #E6E6E6);
  border-radius: 100px;
}

.quantity-control {
  display: flex;
  align-items: center;
  height: 24px;
  border-radius: 3px;
  border: 1px solid #C4C4C4;
  overflow: hidden;
}

.quantity-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: #004879;
  color: #fff;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
  padding: 0;
  font-weight: 600;
}

.quantity-value {
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #4A4A4A;
  font-size: 14px;
  line-height: 20px;
  padding: 0 6px;
  border-left: 1px solid #C4C4C4;
  border-right: 1px solid #C4C4C4;
}

.final-price {
  height: 16px;
  font-size: 14px;
  letter-spacing: 0.4px;
  line-height: 16px;
}

.clear-icon {
  font-size: 20px;
  color: var(--ErrorRed, #D10000);
}

.expand-icon {
  font-size: 24px;
  color: var(--IconGrey, #374957);
}

.unlocking-img{
  height: 16px;
  width: 16px;
}
.unlocking-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  line-height: 14px;
  font-size: 12px; 
}

.deal-price {
  height: 16px;
  color: #000000;
  font-family: Roboto;
  font-size: 14px;
  letter-spacing: 0.4px;
  line-height: 16px;
  /* margin-right: 31px; */
}

.unlocking-deal-container {
  background-color: rgba(132, 188, 59, 0.05);
  display: flex; 
  width: 349px;  
  min-height: 30px;
  padding-right: 8px;
}

.unlocking-deal-container-markup {
  background-color: rgba(132, 188, 59, 0.05);
  height: 30px;
  padding-right: 8px;
  margin-bottom: 8px;
}

.save-text{
  color: #FF1D00;
}

.markup-btn {
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 14.06px;
  letter-spacing: 0.4px;
  color: #004879;
}

.markup-text {
  font-family: Roboto;
  font-style: normal;
  font-size: 14px;
  font-weight: 400;
  line-height: 14.06px;
  letter-spacing: 0.4px;
  color: #979797;
}
</style>
